/**
 * Ripia Permission Service
 *
 * 権限・アクセス制御管理
 * @module auth/permissionService
 */

import logger from '../../utils/logger';
import { TokenStorage } from './shared/tokenStorage';
import type {
  TenantContext,
  TenantPermissionActions,
  TenantResource,
  PlanFeature,
} from './types';

/**
 * Permission Service
 * 権限チェック・アクセス制御
 */
export class PermissionService {
  // ========================
  // Basic Permission Checks
  // ========================

  /**
   * 基本権限チェック（User.permissions配列ベース）
   * @param permission - チェックする権限文字列
   */
  static hasPermission(permission: string): boolean {
    try {
      const user = TokenStorage.getUserData();
      if (!user) return false;

      // Owner権限は全アクセス可能
      if (user.role === 'owner') return true;

      // 特定権限チェック
      return (
        user.permissions?.includes(permission) ||
        user.permissions?.includes('*') ||
        false
      );
    } catch (error) {
      logger.error('権限チェックエラー:', error);
      return false;
    }
  }

  // ========================
  // Tenant Permission Checks
  // ========================

  /**
   * テナント権限チェック（TenantContext.permissionsベース）
   * @param permission - チェックする権限文字列
   * @param resource - リソース名（オプション）
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static hasTenantPermission(
    permission: string,
    resource?: string,
    tenantContext?: TenantContext | null
  ): boolean {
    try {
      // テナントコンテキストが渡されなければStorageから取得
      const context = tenantContext ?? TokenStorage.loadTenantContext();
      if (!context) return false;

      // Owner権限は全アクセス可能
      if (context.tenantRole === 'owner') return true;

      // Legacy permission check - convert to V2 format
      // For backward compatibility, also support the new permission system
      if (
        resource &&
        typeof context.permissions === 'object' &&
        !Array.isArray(context.permissions)
      ) {
        // New format: Record<string, TenantPermissionActions>
        const resourcePermissions = context.permissions[
          resource
        ] as TenantPermissionActions;
        if (!resourcePermissions) return false;

        // Map permission strings to action booleans
        if (permission === 'read' || permission === 'view')
          return resourcePermissions.read === true;
        if (
          permission === 'write' ||
          permission === 'create' ||
          permission === 'update'
        )
          return resourcePermissions.write === true;
        if (permission === 'delete' || permission === 'remove')
          return resourcePermissions.delete === true;
        if (permission === 'admin' || permission === 'manage')
          return resourcePermissions.admin === true;
        if (permission === 'export') return resourcePermissions.export === true;
      }

      // Array permissions (legacy)
      if (Array.isArray(context.permissions)) {
        return (
          context.permissions.includes(permission) ||
          context.permissions.includes('*') ||
          false
        );
      }

      return false;
    } catch (error) {
      logger.error('Tenant permission check failed:', error);
      return false;
    }
  }

  /**
   * V2権限チェック（リソース・アクションベース）
   * @param resource - リソース名
   * @param action - アクション種別
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static hasV2Permission(
    resource: TenantResource,
    action: keyof TenantPermissionActions,
    tenantContext?: TenantContext | null
  ): boolean {
    try {
      const context = tenantContext ?? TokenStorage.loadTenantContext();
      if (!context) return false;

      // Owner権限は全アクセス可能
      if (context.tenantRole === 'owner') return true;

      // Handle both legacy string[] and new Record format
      if (Array.isArray(context.permissions)) {
        // Legacy format: string[]
        const legacyPermission = `${action}:${resource}`;
        return (
          context.permissions.includes(legacyPermission) ||
          context.permissions.includes('*') ||
          context.permissions.includes(resource)
        );
      } else {
        // New format: Record<string, TenantPermissionActions>
        const permissions = context.permissions[resource];
        if (!permissions) return false;

        return permissions[action] === true;
      }
    } catch (error) {
      logger.error('V2 Permission check failed:', error);
      return false;
    }
  }

  // ========================
  // Feature Checks
  // ========================

  /**
   * プランフィーチャーチェック
   * @param feature - チェックするフィーチャー
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static hasTenantFeature(
    feature: PlanFeature,
    tenantContext?: TenantContext | null
  ): boolean {
    try {
      const context = tenantContext ?? TokenStorage.loadTenantContext();
      if (!context) return false;

      return context.features.includes(feature);
    } catch (error) {
      logger.error('Feature check failed:', error);
      return false;
    }
  }

  // ========================
  // Role Convenience Methods
  // ========================

  /**
   * 現在のユーザーがオーナーかどうか
   */
  static isOwner(): boolean {
    const user = TokenStorage.getUserData();
    if (!user) return false;
    return user.role === 'owner';
  }

  /**
   * 現在のユーザーがテナントオーナーかどうか
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static isTenantOwner(tenantContext?: TenantContext | null): boolean {
    const context = tenantContext ?? TokenStorage.loadTenantContext();
    if (!context) return false;
    return context.tenantRole === 'owner';
  }

  /**
   * 現在のユーザーが管理者かどうか（owner または admin）
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static isAdmin(tenantContext?: TenantContext | null): boolean {
    const context = tenantContext ?? TokenStorage.loadTenantContext();
    if (!context) return false;
    return context.tenantRole === 'owner' || context.tenantRole === 'admin';
  }

  // ========================
  // Permission Convenience Methods
  // ========================

  /**
   * スタッフ管理権限があるかどうか
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static canManageStaff(tenantContext?: TenantContext | null): boolean {
    return this.hasV2Permission(
      'staff' as TenantResource,
      'write',
      tenantContext
    );
  }

  /**
   * レポート閲覧権限があるかどうか
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static canViewReports(tenantContext?: TenantContext | null): boolean {
    return this.hasV2Permission(
      'analytics' as TenantResource,
      'read',
      tenantContext
    );
  }

  /**
   * 予約管理権限があるかどうか
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static canManageReservations(tenantContext?: TenantContext | null): boolean {
    return this.hasV2Permission(
      'reservations' as TenantResource,
      'write',
      tenantContext
    );
  }

  /**
   * 顧客管理権限があるかどうか
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static canManageCustomers(tenantContext?: TenantContext | null): boolean {
    return this.hasV2Permission(
      'customers' as TenantResource,
      'write',
      tenantContext
    );
  }

  /**
   * サービス管理権限があるかどうか
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static canManageServices(tenantContext?: TenantContext | null): boolean {
    return this.hasV2Permission(
      'services' as TenantResource,
      'write',
      tenantContext
    );
  }

  /**
   * 設定管理権限があるかどうか
   * @param tenantContext - テナントコンテキスト（外部から注入可能）
   */
  static canManageSettings(tenantContext?: TenantContext | null): boolean {
    return this.hasV2Permission(
      'settings' as TenantResource,
      'write',
      tenantContext
    );
  }
}

export default PermissionService;
